/**
 * «Рахунки обліку НА» (0447, рішення власника 2026-08-31): org-дефолти
 * рахунків малоцінки + org-перебивка рахунків груп ОЗ/НМА. Резолв у
 * проведенні: картка > ці налаштування > платформенна група > хардкод.
 * Внизу — аудит (Норма #24): картки з нестандартними рахунками.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import { assetAccountSettings, assetGroups, type AssetGroupOverride } from '@/api/assets';
import { ASSET_KIND_UA } from '@/api/assetsRegistry';
import { useDefaults } from '@/store/defaults';
import TchTable from '@/shared/components/TchTable';

const KIND_LABEL: Record<string, string> = ASSET_KIND_UA as never;

export default function AssetAccountSettingsPage() {
  const navigate = useNavigate();
  const defaultOrgId = useDefaults((s) => s.defaultOrgId);
  const [orgId, setOrgId] = useState(defaultOrgId || '');
  const qc = useQueryClient();
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const faGroupsQ = useQuery({ queryKey: ['asset-groups', 'FIXED_ASSET'], queryFn: () => assetGroups.list({ kind: 'FIXED_ASSET', limit: 200 }) });
  const iaGroupsQ = useQuery({ queryKey: ['asset-groups', 'INTANGIBLE'], queryFn: () => assetGroups.list({ kind: 'INTANGIBLE', limit: 200 }) });
  const settingsQ = useQuery({
    queryKey: ['asset-account-settings', orgId],
    queryFn: () => assetAccountSettings.get(orgId),
    enabled: !!orgId,
  });
  // Аудит: серверна пагінація (карток можуть бути тисячі) + фільтр виду.
  const [auditKind, setAuditKind] = useState('');
  const [auditLimit, setAuditLimit] = useState(200);
  const auditQ = useQuery({
    queryKey: ['asset-account-nonstandard', orgId, auditKind, auditLimit],
    queryFn: () => assetAccountSettings.nonstandard(orgId, { kind: auditKind || undefined, limit: auditLimit }),
    enabled: !!orgId,
  });
  const auditRows = auditQ.data?.items ?? [];
  const auditTotal = auditQ.data?.total ?? 0;

  const [lvAcc, setLvAcc] = useState('');
  const [lvExp, setLvExp] = useState('');
  const [ov, setOv] = useState<Record<string, { acc: string; depr: string }>>({});
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => {
    const d = settingsQ.data;
    if (!d) return;
    setLvAcc(d.lv_account_code ?? '');
    setLvExp(d.lv_expense_account_code ?? '');
    const m: Record<string, { acc: string; depr: string }> = {};
    d.group_overrides.forEach((o) => { m[o.asset_group_id] = { acc: o.account_code ?? '', depr: o.depreciation_account_code ?? '' }; });
    setOv(m);
    setMsg(null);
  }, [settingsQ.data]);

  const save = useMutation({
    mutationFn: () => {
      const group_overrides: AssetGroupOverride[] = Object.entries(ov)
        .filter(([, v]) => v.acc.trim() || v.depr.trim())
        .map(([gid, v]) => ({ asset_group_id: gid, account_code: v.acc.trim() || null, depreciation_account_code: v.depr.trim() || null }));
      return assetAccountSettings.put({
        organization_id: orgId,
        lv_account_code: lvAcc.trim() || null,
        lv_expense_account_code: lvExp.trim() || null,
        group_overrides,
      });
    },
    onSuccess: () => { setMsg('✓ Збережено'); qc.invalidateQueries({ queryKey: ['asset-account-settings'] }); qc.invalidateQueries({ queryKey: ['asset-account-nonstandard'] }); },
    onError: (e: any) => setMsg(e?.response?.data?.detail ?? String(e)),
  });

  const groups = useMemo(() => [
    ...(faGroupsQ.data?.items ?? []).map((g) => ({ ...g, kind: 'ОЗ' })),
    ...(iaGroupsQ.data?.items ?? []).map((g) => ({ ...g, kind: 'НМА' })),
  ], [faGroupsQ.data, iaGroupsQ.data]);

  return (
    <div className="card">
      <div className="toolbar" style={{ gap: '0.4rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Рахунки обліку НА</h2>
        <button className="primary" style={{ marginLeft: 'auto' }}
          disabled={!orgId || save.isPending} onClick={() => save.mutate()}>зберегти</button>
      </div>
      {/* 2026-08-31 (рішення власника): фільтр організації — під назвою сторінки. */}
      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)} style={{ width: 260 }}>
          <option value="">— організація —</option>
          {orgList.data?.items.map((o) => <option key={o.id} value={o.id}>{o.name} — {o.code}</option>)}
        </select>
      </div>
      {msg && <p style={{ color: msg.startsWith('✓') ? '#15803d' : '#dc2626' }}>{typeof msg === 'string' ? msg : JSON.stringify(msg)}</p>}
      {!orgId && <p style={{ color: 'var(--muted)' }}>Оберіть організацію.</p>}
      {orgId && (
        <>
          <p style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
            Порядок підбору рахунку при проведенні: <b>картка активу → ці налаштування → платформенна група → типовий</b>.
            Порожнє поле = діє наступний рівень.
          </p>
          <h3 style={{ margin: '0.75rem 0 0.25rem' }}>Малоцінка (МШП / МНМА)</h3>
          <div className="doc-grid">
            <div className="field" style={{ gridColumn: 'span 6' }}>
              <label>Рахунок обліку активу <span style={{ color: 'var(--muted)', fontWeight: 400 }}>(типово 22)</span></label>
              <input value={lvAcc} maxLength={10} placeholder="22" onChange={(e) => setLvAcc(e.target.value)} />
            </div>
            <div className="field" style={{ gridColumn: 'span 6' }}>
              <label>Рахунок витрат за замовчуванням <span style={{ color: 'var(--muted)', fontWeight: 400 }}>(типово 92)</span></label>
              <input value={lvExp} maxLength={10} placeholder="92" onChange={(e) => setLvExp(e.target.value)} />
            </div>
          </div>
          <h3 style={{ margin: '1rem 0 0.25rem' }}>Групи ОЗ / НМА — перебивка рахунків організації</h3>
          <TchTable style={{ maxWidth: 900 }}>
            <thead>
              <tr style={{ textTransform: 'none' }}>
                <th style={{ width: 60 }}>Вид</th><th>Група</th>
                <th style={{ width: 140 }}>Стандарт (актив / знос)</th>
                <th style={{ width: 140 }}>Рахунок активу</th>
                <th style={{ width: 140 }}>Рахунок зносу</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => (
                <tr key={g.id}>
                  <td>{g.kind}</td>
                  <td>{g.name_uk}</td>
                  <td style={{ color: 'var(--muted)' }}>{g.accounting_account_code} / {g.depreciation_account_code ?? '—'}</td>
                  <td><input value={ov[g.id]?.acc ?? ''} maxLength={10} placeholder={g.accounting_account_code}
                    onChange={(e) => setOv((m) => ({ ...m, [g.id]: { acc: e.target.value, depr: m[g.id]?.depr ?? '' } }))} /></td>
                  <td><input value={ov[g.id]?.depr ?? ''} maxLength={10} placeholder={g.depreciation_account_code ?? ''}
                    onChange={(e) => setOv((m) => ({ ...m, [g.id]: { acc: m[g.id]?.acc ?? '', depr: e.target.value } }))} /></td>
                </tr>
              ))}
            </tbody>
          </TchTable>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', margin: '1.25rem 0 0.25rem' }}>
            <h3 style={{ margin: 0 }}>Картки з нестандартними рахунками{auditTotal > 0 && <span style={{ color: 'var(--muted)', fontWeight: 400 }}> ({auditTotal})</span>}</h3>
            <select value={auditKind} onChange={(e) => { setAuditKind(e.target.value); setAuditLimit(200); }}
              style={{ width: 200, flex: '0 0 auto' }} title="Фільтр за видом НА">
              <option value="">Усі види</option>
              <option value="FIXED_ASSET">Основні засоби (ОЗ)</option>
              <option value="INTANGIBLE">Нематеріальні активи</option>
              <option value="LOW_VALUE">МШП / МНМА</option>
            </select>
          </div>
          <p style={{ color: 'var(--muted)', fontSize: '0.82rem', margin: '0 0 0.4rem' }}>
            Аудит: рахунок у картці відрізняється від діючого дефолту (наприклад, приїхав з 1С). Це не помилка — перевірте, чи так задумано. Клік відкриває картку.
          </p>
          {auditRows.length === 0 && <p style={{ color: 'var(--muted)' }}>Немає — всі картки за дефолтами.</p>}
          {auditRows.length > 0 && (
            <TchTable style={{ maxWidth: 900 }}>
              <thead>
                <tr style={{ textTransform: 'none' }}>
                  <th style={{ width: 40 }}></th><th style={{ width: 90 }}>Вид</th>
                  <th style={{ width: 110 }}>Інв. №</th><th>Назва</th>
                  <th style={{ width: 130 }}>Поле</th>
                  <th style={{ width: 100 }}>У картці</th><th style={{ width: 100 }}>Дефолт</th>
                </tr>
              </thead>
              <tbody>
                {auditRows.map((r, i) => (
                  <tr key={i} style={{ cursor: 'pointer' }} onClick={() => navigate(`/assets/card/${r.kind}/${r.id}`)}>
                    <td>{r.external_source && <span title="Імпортовано з 1С" style={{ color: '#2563eb', border: '1px solid var(--badge-blue-bd)', borderRadius: 4, fontSize: '0.7rem', fontWeight: 700, padding: '0 4px' }}>I</span>}</td>
                    <td>{KIND_LABEL[r.kind] ?? r.kind}</td>
                    <td>{r.inventory_number}</td>
                    <td>{r.name}</td>
                    <td>{r.field === 'account' ? 'рахунок активу' : r.field === 'depreciation' ? 'рахунок зносу' : 'рахунок витрат'}</td>
                    <td style={{ fontWeight: 600 }}>{r.card_code}</td>
                    <td style={{ color: 'var(--muted)' }}>{r.default_code ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </TchTable>
          )}
          {auditRows.length < auditTotal && (
            <button style={{ marginTop: '0.5rem' }} onClick={() => setAuditLimit((l) => Math.min(l + 200, 500))}
              disabled={auditLimit >= 500}
              title={auditLimit >= 500 ? 'Показано максимум 500 — звузьте фільтром виду' : undefined}>
              показати ще (показано {auditRows.length} з {auditTotal})
            </button>
          )}
        </>
      )}
    </div>
  );
}
